import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';

/**
 * DiceCheckPattern - Generic standalone dice roll against a table or threshold.
 *
 * Use cases:
 * - Age of Umbra: Lurking Darkness (d12 roll table for resting outside Sacred Pyres)
 * - Any frame needing a manual "roll and check" mechanic
 *
 * Config properties:
 * - displayName: UI label
 * - description: explanatory text
 * - icon: emoji icon
 * - dieType: 'd12', 'd20', or 'fear' (resolves from character.fearDie)
 * - rollTable: (optional) [{ min, max, result, description }]
 * - threshold: (optional) { value, comparison: 'lessThanOrEqual'|'greaterThanOrEqual', onPass, onFail }
 * - colorClass: Tailwind classes
 */
const DiceCheckPattern = ({
  frameId,
  mechanicId,
  config,
  character,
  updateCharacter,
  onClose,
}) => {
  const [rollResult, setRollResult] = useState(null);
  const [isRolling, setIsRolling] = useState(false);

  const resolveDieType = useCallback(() => {
    if (config.dieType === 'fear') return character?.fearDie || 'd6';
    return config.dieType || 'd12';
  }, [config.dieType, character]);

  const rollDie = useCallback((dieStr) => {
    const sides = parseInt(dieStr.replace('d', ''), 10) || 6;
    return Math.ceil(Math.random() * sides);
  }, []);

  const findTableEntry = useCallback((roll) => {
    if (!config.rollTable) return null;
    return config.rollTable.find(entry => roll >= entry.min && roll <= entry.max) || null;
  }, [config.rollTable]);

  const handleRoll = useCallback(() => {
    setIsRolling(true);

    setTimeout(() => {
      const dieType = resolveDieType();
      const roll = rollDie(dieType);
      const tableEntry = findTableEntry(roll);

      let passed = null;
      if (config.threshold) {
        const { value, comparison } = config.threshold;
        if (comparison === 'lessThanOrEqual') passed = roll <= value;
        else if (comparison === 'greaterThanOrEqual') passed = roll >= value;
      }

      setRollResult({ dieType, roll, tableEntry, passed });
      setIsRolling(false);

      if (tableEntry) {
        toast(tableEntry.result, { icon: config.icon || '🎲', duration: 4000 });
      }
    }, 800);
  }, [resolveDieType, rollDie, findTableEntry, config]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-xl font-bold text-purple-400 mb-1">
          {config.icon || '🎲'} {config.displayName || 'Dice Check'}
        </h3>
        {config.description && (
          <p className="text-sm text-gray-400">{config.description}</p>
        )}
      </div>

      {/* Die Info */}
      <div className={`relative p-6 rounded-lg border-2 text-center ${config.colorClass || 'bg-purple-900/20 border-purple-600'}`}>
        {rollResult ? (
          <>
            <div className="text-5xl font-bold text-purple-400 mb-2">
              {rollResult.roll}
            </div>
            <p className="text-sm text-purple-300">
              Rolled {rollResult.dieType.toUpperCase()}
            </p>
          </>
        ) : (
          <p className="text-sm text-purple-300">
            Roll {resolveDieType().toUpperCase()} to check
          </p>
        )}

        {isRolling && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <div className="text-4xl animate-bounce mb-2">🎲</div>
              <p className="text-sm text-purple-400 font-medium">Rolling...</p>
            </div>
          </div>
        )}
      </div>

      {/* Table Result */}
      {rollResult?.tableEntry && (
        <div className="bg-gray-800/50 p-4 rounded-lg border border-purple-600/50">
          <p className="text-sm font-bold text-purple-300 mb-1">{rollResult.tableEntry.result}</p>
          {rollResult.tableEntry.description && (
            <p className="text-xs text-gray-300">{rollResult.tableEntry.description}</p>
          )}
        </div>
      )}

      {/* Threshold Result */}
      {rollResult && rollResult.passed !== null && (
        <div className={`p-4 rounded-lg border ${
          rollResult.passed
            ? 'bg-green-900/20 border-green-600/50'
            : 'bg-red-900/20 border-red-600/50'
        }`}>
          <p className={`text-sm font-bold ${rollResult.passed ? 'text-green-300' : 'text-red-300'}`}>
            {rollResult.passed
              ? (config.threshold?.onPass?.description || 'Check passed!')
              : (config.threshold?.onFail?.description || 'Check failed.')}
          </p>
        </div>
      )}

      {/* Roll Button */}
      <DomainCardButton
        onClick={handleRoll}
        disabled={isRolling}
        variant="primary"
        className="w-full"
      >
        {rollResult ? 'Roll Again' : `Roll ${resolveDieType().toUpperCase()}`}
      </DomainCardButton>

      {/* Close */}
      <DomainCardButton onClick={onClose} variant="secondary" className="w-full">
        Close
      </DomainCardButton>
    </div>
  );
};

export default DiceCheckPattern;
